/**
 * @andavo/staffing-engine — the CSM staffing model.
 *
 * Pure functions over plain data: no I/O, no framework, no runtime deps. Two
 * consumers share this ONE implementation so they cannot disagree —
 * customer-management-api computes authoritative snapshots and exports with it,
 * apps/web re-tiers the book live as a threshold moves.
 *
 * Scope today: tiering, balanced assignment, portfolios, coverage, and the
 * hours/FTE math. The financial model (revenue/profit/staffing-cost guardrail)
 * and growth projection are the next layer and are deliberately NOT here yet —
 * see docs/architecture/csm-staffing-plan.md.
 */

/**
 * WHICH ENGINE produced a result, recorded on every snapshot.
 *
 * A behaviour change in this package is exactly when a replay of an old snapshot
 * legitimately disagrees with the stored portfolios, and this is what tells that
 * apart from drifted data or tampering. It lives HERE, beside the code it
 * describes, rather than in a caller: a version string maintained by apps/web
 * would name whatever apps/web believed it was using.
 *
 * Kept in step with package.json by `tests/engine-version.test.ts`, which fails
 * if the two disagree — a constant that silently lags the package it names is
 * worse than no constant, because it reads as a measurement.
 */
export const ENGINE_VERSION = '0.1.0';

export type {
  Account,
  AssignmentCoverage,
  BalanceBy,
  Pins,
  Portfolio,
  StaffingConfig,
  TierPins,
  CsmId,
  StaffingResult,
  TierDef,
} from './types.ts';
export { SENTINEL_CSMS } from './types.ts';

export {
  assertTiersOrdered,
  bandTiers,
  bucketByTier,
  isNamedTier,
  tierOf,
} from './tiering.ts';
export {
  assertInputsValid,
  assertSlotsUnique,
  computeCoverage,
  computePortfolios,
  portfolioAccounts,
} from './assign.ts';
export type { PortfolioLoad, TierLoad } from './capacity.ts';
export {
  ftesNeeded,
  monthlyHours,
  portfolioLoad,
  tierLoads,
} from './capacity.ts';
